import { Box, Button, Dialog, DialogContent, DialogTitle, TextField, Typography, } from "@mui/material";

import { DatePicker, LocalizationProvider, TimePicker, } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useState } from "react";
import { MobileNumberField } from "../common/form/MobileNumberField";
import UpperCaseTextField from "../../modules/common/shared/components/UpperCaseTextField";
import { SharedDialog } from "../../modules/common/shared/SharedDialog";
import { demoService } from "./services/demo.service";
import { DemoRequestData } from "./types";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const RequestDemoDialog = ({ open, onClose }: Props) => {

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  const [formData, setFormData] = useState<DemoRequestData>({
    schoolName: undefined,
    contactPerson: undefined,
    phone: '',
    message: undefined,
    preferredDemoDate: undefined,
    preferredDemoTime: undefined,
  });

  const handleSubmit = async (e: any) => {
    try {
      setLoading(true);

      e.preventDefault();
      const schoolName = formData.schoolName;
      const contactPerson = formData.contactPerson;
      const phone = formData.phone;
      const message = formData.message;
      const preferredDemoDate = formData.preferredDemoDate;
      const preferredDemoTime = formData.preferredDemoTime;
      await demoService.requestDemo({
        schoolName,
        contactPerson,
        phone,
        message,
        preferredDemoDate,
        preferredDemoTime
      });

      onClose(); // close modal
      setSuccessOpen(true);
      setFormData({
        schoolName: undefined,
        contactPerson: undefined,
        phone: '',
        message: undefined,
        preferredDemoDate: undefined,
        preferredDemoTime: undefined,
      });
    } catch (e) {
      setErrorOpen(true);
      console.log(e)
    } finally {
      setLoading(false);
    }
  };

  const getNextSaturday = () => {
    const today = dayjs();
    const daysUntilSaturday = (6 - today.day()) % 7; // 6 is Saturday (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    return today.add(daysUntilSaturday, 'day').startOf('day'); // Set to the next Saturday's date at 00:00
  };

  return (
    <>
      <SharedDialog
        message={"Something went wrong. Please try again."}
        dialogState={errorOpen}
        setDialogState={value => setErrorOpen(value)}
        proceedButtonRequired={false}
        cancelOptionText="Ok"
      />
      <SharedDialog
        message={"Demo request submitted! Our team will contact you shortly."}
        dialogState={successOpen}
        setDialogState={value => setSuccessOpen(value)}
        proceedButtonRequired={false}
        cancelOptionText="Ok"
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request a Free Demo
        </DialogTitle>

        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Tell us a little about your school. We’ll get in touch shortly.
          </Typography>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
            onSubmit={handleSubmit}
          >
            <UpperCaseTextField
              value={formData.schoolName}
              setValue={(value) => setFormData({ ...formData, schoolName: value })}
              label="School Name"
              required={true}
            />
            <UpperCaseTextField
              value={formData.contactPerson}
              setValue={(value) => setFormData({ ...formData, contactPerson: value })}
              label="Contact Person"
              required={true}
              minLen={3}
            />
            <MobileNumberField
              label="Phone Number"
              value={formData.phone}
              onChange={(phoneNo) => setFormData({ ...formData, phone: phoneNo })}
              required={true}
              fullWidth
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 2,
                }}
              >

                <DatePicker
                  label="Preferred Demo Date"
                  slotProps={{
                    textField: (params) => ({
                      ...params,
                      name: "dob",
                      size: "small",
                      required: true,
                    }),
                  }}
                  sx={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={getNextSaturday()}
                  readOnly
                />

                <TimePicker
                  label="Preferred Demo Time"
                  value={formData.preferredDemoTime ? dayjs(formData.preferredDemoTime, 'HH:mm') : null}
                  slotProps={{
                    textField: (params) => ({
                      ...params,
                      name: "dob",
                      size: "small",
                      required: true,
                    }),
                  }}
                  sx={{ width: "100%" }}
                  onChange={(newValue) => setFormData({ ...formData, preferredDemoTime: newValue ? newValue.format('HH:mm') : undefined })}
                />
              </Box>
            </LocalizationProvider>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
              }}
            >
              <Button onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                size="small"
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>

            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
